import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Database, Download } from "lucide-react";
import { useAppStore } from "@/store/useAppStore";
import { TOP_N_OPTIONS } from "@/lib/config";
import { toast } from "@/components/ui/toast";
import { downloadText } from "@/lib/utils/download";
import {
  serializeCombinationsCsv,
  serializeEventsCsv,
} from "@/lib/storage/CsvProvider";
import type {
  StorageProviderKind,
  UserPermission,
} from "@/types";

/**
 * Administration / Configuration screen.
 * Exposed parameters, tracking toggle, storage provider, analytics depth,
 * permissions and a destructive reset. Also bound to the Tableau `configure`
 * entry point so admins can open it from the extension's context menu.
 */
export function AdminPanel({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const config = useAppStore((s) => s.config);
  const descriptors = useAppStore((s) => s.descriptors);
  const allDescriptors = useAppStore((s) => s.allDescriptors);
  const update = useAppStore((s) => s.updateConfig);
  const resetAnalytics = useAppStore((s) => s.resetAnalytics);
  const combinations = useAppStore((s) => s.combinations);
  const events = useAppStore((s) => s.events);
  const activeProvider = useAppStore((s) => s.storage?.kind);
  const tableauWorkbook = useAppStore((s) => s.tableau?.workbookName);

  const today = new Date().toISOString().slice(0, 10);
  function exportCombinations() {
    downloadText(`combinations-${today}.csv`, serializeCombinationsCsv(combinations));
    toast.success("Exported combinations.csv", `${combinations.length} rows`);
  }
  function exportEvents() {
    downloadText(`usage-events-${today}.csv`, serializeEventsCsv(events));
    toast.success("Exported usage-events.csv", `${events.length} rows`);
  }

  /** Verify the storage API and create/validate the table set. */
  async function initializeDb() {
    const base = (config.restApiBaseUrl || "/api").replace(/\/$/, "");
    try {
      const res = await fetch(`${base}/init`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(config.storageProvider === "cloud" ? { "X-SPC-Backend": "kv" } : {}),
          ...(config.restApiKey ? { Authorization: `Bearer ${config.restApiKey}` } : {}),
          ...(config.dbxHost ? { "X-DBX-Host": config.dbxHost } : {}),
          ...(config.dbxToken ? { "X-DBX-Token": config.dbxToken } : {}),
          ...(config.dbxWarehouse ? { "X-DBX-Warehouse": config.dbxWarehouse } : {}),
          ...(config.dbxCatalog ? { "X-DBX-Catalog": config.dbxCatalog } : {}),
          ...(config.dbxSchema ? { "X-DBX-Schema": config.dbxSchema } : {}),
        },
        body: JSON.stringify({ table: config.dbTable, mode: config.dbTableMode }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.error ?? `${res.status} ${res.statusText}`);
      toast.success("Database ready", body.message ?? "Tables verified.");
    } catch (err) {
      toast.error(
        "Database setup failed",
        err instanceof Error ? err.message : String(err),
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle>Configuration</DialogTitle>
          <DialogDescription>Administer how the extension behaves for everyone using this dashboard.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Parameter exposure */}
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold">Included parameters</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {descriptors.length} of {allDescriptors.length} shown
                </span>
                <Button variant="ghost" size="sm" onClick={() => update({ exposedParameterIds: [] })}>
                  Include all
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => update({ exposedParameterIds: ["__none__"] })}
                >
                  Clear
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Toggle which dashboard parameters users can control. Turn one off to hide it from the panel.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {allDescriptors.map((d) => {
                const checked =
                  config.exposedParameterIds.length === 0 ||
                  config.exposedParameterIds.includes(d.id);
                return (
                  <label key={d.id} className="glass-sm flex items-center justify-between gap-2 px-3 py-2">
                    <span className="truncate text-sm" title={d.name}>{d.name}</span>
                    <Switch
                      checked={checked}
                      onCheckedChange={(v) => toggleExposed(d.id, v)}
                    />
                  </label>
                );
              })}
            </div>
          </section>

          {/* Required parameters */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Required parameters</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {descriptors.map((d) => (
                <label key={d.id} className="glass-sm flex items-center justify-between gap-2 px-3 py-2">
                  <span className="truncate text-sm">{d.name}</span>
                  <Switch
                    checked={config.requiredParameterIds.includes(d.id)}
                    onCheckedChange={(v) => toggleRequired(d.id, v)}
                  />
                </label>
              ))}
            </div>
          </section>

          {/* Toggles */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">Behavior</h3>
            <Row label="Enable usage tracking" description="Capture combinations & analytics on every Go.">
              <Switch checked={config.trackingEnabled} onCheckedChange={(v) => update({ trackingEnabled: v })} />
            </Row>
            <Row label="Save user name" description="Record who ran each combination. Off = stored as 'Anonymous'.">
              <Switch checked={config.saveUserName} onCheckedChange={(v) => update({ saveUserName: v })} />
            </Row>
            <Row label="AI recommendations" description="Surface 'analysts also used' suggestions.">
              <Switch checked={config.enableAiRecommendations} onCheckedChange={(v) => update({ enableAiRecommendations: v })} />
            </Row>
          </section>

          {/* Storage */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">Storage provider</h3>
            <Select value={config.storageProvider} onValueChange={(v) => update({ storageProvider: v as StorageProviderKind })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cloud">Cloud (built-in — shared, no setup)</SelectItem>
                <SelectItem value="csv">CSV (browser, exportable)</SelectItem>
                <SelectItem value="indexeddb">IndexedDB (browser, scalable)</SelectItem>
                <SelectItem value="local">Local Storage (browser, simple)</SelectItem>
                <SelectItem value="tableau-settings">Tableau Settings (in-workbook, shared)</SelectItem>
                <SelectItem value="rest">Database (Databricks / SQL Server / any DB)</SelectItem>
              </SelectContent>
            </Select>
            {config.storageProvider === "cloud" && (
              <p className="text-[11px] text-muted-foreground">
                History is stored with the extension’s own web service — shared by every
                user of this dashboard, survives reopening and republishing, and needs no
                credentials or database. Nothing else to configure.
              </p>
            )}
            {(config.storageProvider === "rest" || config.storageProvider === "cloud") &&
              activeProvider !== config.storageProvider && (
              <p className="text-xs text-warning">
                {config.storageProvider === "cloud" ? "Cloud storage" : "Database"} selected,
                but the extension is currently running on browser storage — the storage
                service isn’t reachable{config.storageProvider === "rest" ? " or its connection details aren’t configured yet" : ""}.
                Reload the extension after fixing.
              </p>
            )}
            {config.storageProvider === "rest" && (
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="dbx-host">Server (workspace host)</Label>
                    <Input
                      id="dbx-host"
                      value={config.dbxHost ?? ""}
                      placeholder="adb-1234567890.12.azuredatabricks.net"
                      onChange={(e) => update({ dbxHost: e.target.value || undefined })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="dbx-warehouse">SQL warehouse ID</Label>
                    <Input
                      id="dbx-warehouse"
                      value={config.dbxWarehouse ?? ""}
                      placeholder="e.g. 1a2b3c4d5e6f7g8h"
                      onChange={(e) => update({ dbxWarehouse: e.target.value || undefined })}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dbx-token">Access token (password)</Label>
                  <Input
                    id="dbx-token"
                    type="password"
                    value={config.dbxToken ?? ""}
                    placeholder="dapi…"
                    onChange={(e) => update({ dbxToken: e.target.value || undefined })}
                  />
                  <p className="text-[11px] text-warning">
                    Stored in the workbook — use a token scoped to only this catalog/schema,
                    not a personal all-access token.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="dbx-catalog">Catalog (optional)</Label>
                    <Input
                      id="dbx-catalog"
                      value={config.dbxCatalog ?? ""}
                      placeholder="main"
                      onChange={(e) => update({ dbxCatalog: e.target.value || undefined })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="dbx-schema">Schema (optional)</Label>
                    <Input
                      id="dbx-schema"
                      value={config.dbxSchema ?? ""}
                      placeholder="default"
                      onChange={(e) => update({ dbxSchema: e.target.value || undefined })}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rest-url">Storage API base URL (advanced)</Label>
                  <Input
                    id="rest-url"
                    value={config.restApiBaseUrl ?? ""}
                    placeholder="Leave empty — uses this site's built-in web API"
                    onChange={(e) => update({ restApiBaseUrl: e.target.value })}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Only needed for a custom backend (e.g. an internal SQL Server API).
                    Databricks works fully hosted with the fields above.
                  </p>
                </div>
              </div>
            )}
            {(config.storageProvider === "rest" || config.storageProvider === "cloud") && (
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="db-table">Table / history name</Label>
                    <Input
                      id="db-table"
                      value={config.dbTable}
                      placeholder="smart_rewrite_history"
                      onChange={(e) => update({ dbTable: e.target.value })}
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {config.storageProvider === "cloud"
                        ? "Give each workbook its own name to keep histories separate — or reuse a name across workbooks to share one history."
                        : `Base name — data lands in ${config.dbTable || "…"}_combinations, _events, _favorites.`}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Table setup</Label>
                    <Select
                      value={config.dbTableMode}
                      onValueChange={(v) => update({ dbTableMode: v as "existing" | "create" })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="create">Create new table</SelectItem>
                        <SelectItem value="existing">Use existing table</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={initializeDb}>
                    <Database className="h-4 w-4" /> Connect &amp; initialize
                  </Button>
                  {tableauWorkbook && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => update({ dbTable: suggestTableName(tableauWorkbook) })}
                      title="Name this history after the current workbook"
                    >
                      Use workbook name
                    </Button>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* CSV export */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Export as CSV</h3>
            <p className="text-xs text-muted-foreground">
              Download the stored data as CSV. Columns match the SQL schema, so files load straight into a database later.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={exportCombinations}>
                <Download className="h-4 w-4" /> Combinations ({combinations.length})
              </Button>
              <Button variant="outline" size="sm" onClick={exportEvents}>
                <Download className="h-4 w-4" /> Usage events ({events.length})
              </Button>
            </div>
          </section>

          {/* Analytics depth + permission */}
          <section className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Top Combinations shown</Label>
              <Select value={String(config.topN)} onValueChange={(v) => update({ topN: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TOP_N_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>Top {n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                How many combinations the Top Combinations list keeps visible.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Viewer permission</Label>
              <Select value={config.permission} onValueChange={(v) => update({ permission: v as UserPermission })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">View only</SelectItem>
                  <SelectItem value="execute">Execute</SelectItem>
                  <SelectItem value="favorites">Save favorites</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </section>

          {/* Theme + danger zone */}
          <section className="flex items-center justify-between gap-4 border-t border-border/60 pt-4">
            <div className="flex items-center gap-2">
              <Label>Theme</Label>
              <Select value={config.theme} onValueChange={(v) => update({ theme: v as "light" | "dark" | "system" })}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="destructive"
              onClick={async () => {
                await resetAnalytics();
                toast.warning("Analytics reset", "All combinations, events & favorites cleared.");
              }}
            >
              Reset all data
            </Button>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );

  function toggleExposed(id: string, on: boolean) {
    const validIds = new Set(allDescriptors.map((d) => d.id));
    // Empty list means "all exposed". First explicit toggle materialises the set.
    const current =
      config.exposedParameterIds.length === 0
        ? allDescriptors.map((d) => d.id)
        : // Drop any stale/sentinel ids (e.g. the "Clear" marker).
          config.exposedParameterIds.filter((x) => validIds.has(x));
    const draftNext = on
      ? [...new Set([...current, id])]
      : current.filter((x) => x !== id);
    // Keep a non-empty sentinel when the user has explicitly excluded everything,
    // so it doesn't collapse back to the "expose all" default.
    const next = draftNext.length === 0 ? ["__none__"] : draftNext;
    update({ exposedParameterIds: next });
  }

  function toggleRequired(id: string, on: boolean) {
    const next = on
      ? [...new Set([...config.requiredParameterIds, id])]
      : config.requiredParameterIds.filter((x) => x !== id);
    update({ requiredParameterIds: next });
  }
}

/** Workbook name → safe table/space name, e.g. "Sales KPIs (2026)" → "sales_kpis_2026". */
function suggestTableName(workbookName: string): string {
  const slug = workbookName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
  return slug || "smart_rewrite_history";
}

function Row({
  label,
  description,
  children,
}: {
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="glass-sm flex items-center justify-between gap-4 px-3 py-2">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {children}
    </div>
  );
}
